import React, { useMemo } from 'react'
import { useModal } from 'view/shared/modal'
import { SplitButtonDropdown, ActionItem, SplitButtonType } from 'view/components/menus'
import { DealDetail, EntityType, DealActions, ServicingType } from 'api'
import { useAppDispatch } from 'utils/redux'
import { ActionsButtonProps } from 'view/components/datagrid'
import { Translation, Locale } from 'locale'
import { WindowActions } from 'epics/windowEpic'
import {
  getDealTitle,
  canServicingSend,
  canServicingRevertAttributes,
  canServicingAccept,
  canServicingReject,
  canServicingWithdraw
} from 'view/routes/deals/components/utils'
import Ticket from '../../deals/components/DealTicket'
import { isOpenFin } from 'utils'
import { ServicingRerateDialog, ServicingTerminationDialog } from 'view/shared/servicingDialogs'
import { useMidTradeEventsView } from '../Screen/MidTradeEventsViewContext'
import { useIsAdmin } from 'view/hooks'
import { MID_TRADE_VIEW_INCOMING, MID_TRADE_VIEW_INITIATED } from 'selectors/midTradeEventsSelectors'
import { RelatedDealsThunks } from 'thunks'

export interface MidTradeEventActionsButtonProps extends ActionsButtonProps<DealDetail> {
  disabled?: boolean
}

export const MidTradeEventActionsButton = ({ disabled, items }: MidTradeEventActionsButtonProps) => {
  const actions = useActions(items)
  const actionIds = actions.filter(action => action.enabled).map(mainAction => mainAction.id)
  const maxButtons =
    actionIds.includes(Translation.TC_ACTION_ACCEPT) &&
    actionIds.includes(Translation.TC_ACTION_REJECT_TERMINATE) &&
    actionIds.includes(Translation.TC_ACTION_REJECT_RERATE)
      ? 3
      : (actionIds.includes(Translation.TC_ACTION_ACCEPT) && actionIds.includes(Translation.TC_ACTION_REJECT)) ||
        (actionIds.includes(Translation.TC_ACTION_ACCEPT) && actionIds.includes(Translation.TC_ACTION_CANCEL)) ||
        (actionIds.includes(Translation.TC_ACTION_SEND) && actionIds.includes(Translation.TC_ACTION_UNDO))
      ? 2
      : 1

  return <SplitButtonDropdown actions={actions} disabled={disabled} maxButtons={maxButtons} />
}

export const useActions = (items: DealDetail[]): ActionItem[] => {
  const view = useMidTradeEventsView()
  const dispatch = useAppDispatch()
  const isAdminUser = useIsAdmin()
  const { showModal } = useModal()

  return useMemo(
    () => [
      {
        id: Translation.TC_ACTION_SEND,
        label: Locale.translate(Translation.TC_ACTION_SEND),
        type: SplitButtonType.Good,
        onClick: () => items.forEach(i => dispatch(DealActions.servicingSend(i.deal.dealId))),
        enabled: items.every(canServicingSend)
      },
      {
        id: Translation.TC_ACTION_UNDO,
        label: Locale.translate(Translation.TC_ACTION_UNDO),
        type: SplitButtonType.BadLight,
        onClick: () => items.forEach(i => dispatch(DealActions.servicingRevertAttributes(i.deal.dealId))),
        enabled: items.every(canServicingRevertAttributes)
      },
      {
        id: Translation.TC_ACTION_ACCEPT,
        label: Locale.translate(Translation.TC_ACTION_ACCEPT),
        type: SplitButtonType.Good,
        onClick: () => dispatch(DealActions.servicingAcceptBulk(items.map(i => i.deal.dealId))),
        enabled: items.every(item => canServicingAccept(item) && (!isAdminUser || !item.isServicingInitiator))
      },
      {
        id: Translation.TC_ACTION_REJECT,
        label: Locale.translate(Translation.TC_ACTION_REJECT),
        type: SplitButtonType.Bad,
        onClick: () => items.forEach(i => dispatch(DealActions.servicingReject(i.deal.dealId))),
        enabled: items.every(
          item =>
            item.deal.servicingDetails?.servicingType.servicingType !== ServicingType.ROLL_IN_PLACE &&
            canServicingReject(item) &&
            (!isAdminUser || !item.isServicingInitiator)
        )
      },
      {
        id: Translation.TC_ACTION_REJECT_TERMINATE,
        label: Locale.translate(Translation.TC_ACTION_REJECT_TERMINATE),
        type: SplitButtonType.Bad,
        onClick: () => {
          items.forEach(i => dispatch(DealActions.servicingReject(i.deal.dealId)))
          showModal(<ServicingTerminationDialog dealIds={items.map(item => item.deal.dealId)} />)
        },
        enabled: items.every(
          item =>
            item.deal.servicingDetails?.servicingType.servicingType === ServicingType.ROLL_IN_PLACE &&
            canServicingReject(item) &&
            item.deal.currency === items[0].deal.currency
        )
      },
      {
        id: Translation.TC_ACTION_REJECT_RERATE,
        label: Locale.translate(Translation.TC_ACTION_REJECT_RERATE),
        type: SplitButtonType.Bad,
        onClick: () => {
          items.forEach(item => dispatch(DealActions.servicingReject(item.deal.dealId)))
          showModal(<ServicingRerateDialog dealIds={items.map(item => item.deal.dealId)} />)
        },
        enabled: items.every(
          item =>
            item.deal.servicingDetails?.servicingType.servicingType === ServicingType.ROLL_IN_PLACE &&
            canServicingReject(item) &&
            item.deal.currency === items[0].deal.currency
        )
      },
      {
        id: Translation.TC_ACTION_CANCEL,
        label: Locale.translate(Translation.TC_ACTION_CANCEL),
        type: SplitButtonType.BadOutline,
        onClick: () => items.forEach(i => dispatch(DealActions.servicingWithdraw(i.deal.dealId))),
        enabled: items.every(canServicingWithdraw)
      },
      {
        id: Translation.TC_ACTION_OPEN_TICKET,
        label: Locale.translate(Translation.TC_ACTION_OPEN_TICKET),
        onClick: () => {
          items.forEach(dealDetail => {
            if (isOpenFin()) {
              dispatch(WindowActions.openTicketWindow(dealDetail.deal.dealId, EntityType.DEAL, getDealTitle(dealDetail)))
            } else {
              showModal(<Ticket dealId={dealDetail.deal.dealId} />)
            }
          })
        },
        enabled: true
      },
      {
        id: Translation.TC_ACTION_TRADE_HISTORY,
        label: Locale.translate(Translation.TC_ACTION_TRADE_HISTORY),
        onClick: () => {
          if (view === MID_TRADE_VIEW_INITIATED) {
            dispatch(RelatedDealsThunks.updateMteInitiatedRelatedDealsSubscription(items[0].deal.originalDealId))
          } else if (view === MID_TRADE_VIEW_INCOMING) {
            dispatch(RelatedDealsThunks.updateMteIncomingRelatedDealsSubscription(items[0].deal.originalDealId))
          }
        },
        enabled: (view === MID_TRADE_VIEW_INITIATED || view === MID_TRADE_VIEW_INCOMING) && items.length === 1
      }
    ],
    [dispatch, items, isAdminUser, showModal, view]
  )
}
